import type { MapSystem } from '@stars-and-ships/api-types';
import { ChevronDown, Shield } from 'lucide-react';
import { useId, useMemo, useState } from 'react';
import {
  type DispatchMission,
  distanceLy,
  formatLy,
  missionBlocker,
  targetsByDistance,
  type Viewer,
} from '../../dispatch';
import { ownerTag } from '../../ownership';
import { inputFrame, Sheet, TextField } from '../ui';

/** What the sheet says while a mission narrows the list (only Reinforce and Transfer do). */
const FILTER_TEXT: Partial<Record<DispatchMission, { hint: string; empty: string }>> = {
  reinforce: {
    hint: 'Your alliance members’ systems. To move ships to your own systems, choose Transfer.',
    empty: 'No alliance member’s system to reinforce.',
  },
  transfer: {
    hint: 'Your own systems. To support an ally, choose Reinforce.',
    empty: 'You have no other system to transfer to.',
  },
};

/**
 * Target field of Send fleet (spec §3.5): opens a sheet with every system, nearest first. With a
 * `mission` (Reinforce or Transfer), only the systems that mission can go to.
 */
export function TargetPicker({
  systems,
  origin,
  me,
  targetId,
  onChoose,
  mission,
}: {
  systems: MapSystem[];
  origin: MapSystem | undefined;
  me: Viewer | undefined;
  targetId: string;
  onChoose(id: string): void;
  mission?: DispatchMission | undefined;
}) {
  const labelId = useId();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const rows = useMemo(
    () =>
      targetsByDistance(systems, origin, search).filter(
        (r) => !mission || missionBlocker(mission, r.system, me) === null
      ),
    [systems, origin, search, mission, me]
  );
  const filter = mission ? FILTER_TEXT[mission] : undefined;
  const chosen = systems.find((s) => s.id === targetId);
  const viewer = me ? { playerId: me.playerId, allianceId: me.alliance?.id ?? null } : undefined;
  return (
    <div>
      <span
        id={labelId}
        className="block font-display text-xs font-semibold uppercase tracking-[0.14em] text-muted"
      >
        Target
      </span>
      <button
        type="button"
        data-target-choice
        aria-haspopup="dialog"
        aria-label={chosen ? `Target: ${chosen.name} (change)` : 'Choose target'}
        onClick={() => setOpen(true)}
        className={`mt-1 ${inputFrame} flex items-center justify-between gap-2 text-left`}
      >
        {chosen ? (
          <span className="min-w-0 truncate">
            {chosen.name}
            <span className="text-sm text-muted"> · {ownerTag(chosen, viewer)}</span>
          </span>
        ) : (
          <span className="text-faint">Choose a system</span>
        )}
        <span className="flex shrink-0 items-center gap-1 font-mono text-xs tabular-nums text-muted">
          {chosen && origin ? formatLy(distanceLy(origin, chosen)) : null}
          <ChevronDown className="size-4" aria-hidden />
        </span>
      </button>
      <Sheet open={open} title="Choose target" onClose={() => setOpen(false)}>
        <TextField
          type="search"
          label="Find a system"
          placeholder="System or commander"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        {filter ? (
          <p data-target-filter className="mt-3 text-xs text-muted">
            {filter.hint}
          </p>
        ) : null}
        {rows.length === 0 ? (
          <p className="mt-3 text-sm text-muted">
            {filter && !search.trim() ? filter.empty : 'No system matches.'}
          </p>
        ) : null}
        <ul className="mt-3 divide-y divide-line border-y border-line">
          {rows.map(({ system, distanceLy: ly }) => (
            <li key={system.id}>
              <button
                type="button"
                data-target-option={system.id}
                aria-current={system.id === targetId || undefined}
                onClick={() => {
                  onChoose(system.id);
                  setOpen(false);
                }}
                className="flex min-h-11 w-full items-center justify-between gap-3 px-2 py-2 text-left hover:bg-panel-raised aria-[current]:bg-accent/10"
              >
                <span className="min-w-0">
                  <span className="flex items-center gap-1 text-sm text-text">
                    {system.name}
                    {system.ownerShielded ? (
                      <Shield className="size-3.5 text-info" aria-label="Beginner shield" />
                    ) : null}
                  </span>
                  <span className="block truncate text-xs text-muted">
                    {ownerTag(system, viewer)}
                  </span>
                </span>
                <span className="shrink-0 font-mono text-xs tabular-nums text-muted">
                  {formatLy(ly)}
                </span>
              </button>
            </li>
          ))}
        </ul>
      </Sheet>
    </div>
  );
}
